import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogOut, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";

const primaryNav = [
  { label: "Dashboard", path: "/dashboard" },
  { label: "Wishlists", path: "/wishlist" },
  { label: "Events", path: "/events" },
  { label: "Blog", path: "/blog" },
  { label: "Community", path: "/community" },
];

const moreNav = [
  { label: "About Us", path: "/about" },
  { label: "The Waste Crisis", path: "/waste-crisis" },
  { label: "Unboxing Mode", path: "/dashboard/unboxing", authOnly: true },
  { label: "Contact Us", path: "/contact" },
  { label: "Legal", path: "/legal" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-lg border-b border-border">
      <div className="container flex items-center justify-between h-16 max-w-7xl mx-auto px-4">
        {/* Logo — uses public WebP with explicit dimensions */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src="/images/hintsly-logo-transparent.png"
            alt="Hintsly"
            width={80}
            height={80}
            className="h-20 w-20"
            loading="eager"
          />
        </Link>

        {/* Desktop navigation */}
        <nav className="hidden lg:flex items-center gap-1 ml-8" aria-label="Main navigation">
          {primaryNav.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.path)
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {item.label}
            </Link>
          ))}

          {/* More dropdown */}
          <div className="relative">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              onBlur={() => setTimeout(() => setMoreOpen(false), 150)}
              aria-expanded={moreOpen}
              aria-haspopup="true"
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                moreOpen
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              More <ChevronDown className={`h-3.5 w-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
            {moreOpen && (
              <div role="menu" className="absolute right-0 top-full mt-1 w-48 rounded-xl border border-border bg-card shadow-lg py-1 z-50">
                {moreNav
                  .filter((item) => !item.authOnly || user)
                  .map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMoreOpen(false)}
                      className={`block px-4 py-2.5 text-sm transition-colors ${
                        isActive(item.path)
                          ? "bg-accent text-accent-foreground"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
              </div>
            )}
          </div>
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {user ? (
            <Button variant="ghost" size="sm" onClick={handleSignOut} className="hidden sm:inline-flex">
              <LogOut className="h-4 w-4 mr-1" /> Sign Out
            </Button>
          ) : (
            <Button variant="default" size="sm" asChild>
              <Link to="/auth">Sign In</Link>
            </Button>
          )}

          {/* Mobile hamburger — only visible below lg */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
            className="lg:hidden"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {mobileOpen && (
        <nav className="border-t border-border bg-card px-4 pb-4 pt-2 space-y-1 lg:hidden" aria-label="Mobile navigation">
          {[...primaryNav, ...moreNav]
            .filter((item) => !("authOnly" in item && item.authOnly) || user)
            .map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                {item.label}
              </Link>
            ))}
          {user && (
            <button
              onClick={() => { handleSignOut(); setMobileOpen(false); }}
              className="block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors sm:hidden"
            >
              Sign Out
            </button>
          )}
        </nav>
      )}
    </header>
  );
};

export default Header;
